import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';
import {array, boolean, object} from 'yup';

import {isEmpty, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useCreateCustomFieldPropertyMutation} from '@dms/api/core';
import {
  AddFieldDefinitionRequestBody,
  TenantFieldDefinitionResponseBody,
  useAddFieldDefinitionMutation,
  useAddTenantEnumOptionMutation,
  useGetFieldDefinitionListQuery,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, yupString} from 'shared/web';

import {DEFAULT_VALUE_OPTIONS} from '../../constants/DEFAULT_VALUE_OPTIONS';
import {EMPTY_ENUM_VALUE} from '../../constants/EMPTY_ENUM_VALUE';
import {VALUE_TYPE_OPTIONS} from '../../constants/valueTypeOptions';
import {DefinitionFormValues} from '../../types/DefinitionFormValues';
import {transformToBoolean} from '../../utils/transformToBoolean';
import {AdditionalFieldsByType} from './AdditionalFieldsByType';

interface CreateDefinitionFormProps extends RequiredTestIdProps {
  resourceId: string;
  onAfterSubmit?: () => void;
  areDefinitionsEmpty: boolean;
}

export function CreateDefinitionForm(props: CreateDefinitionFormProps) {
  const [createDefinition] = useAddFieldDefinitionMutation();
  const {refetch} = useGetFieldDefinitionListQuery({type: 'tenant'});
  const [addTenantEnumOption] = useAddTenantEnumOptionMutation();

  const [createCustomFieldProperty] = useCreateCustomFieldPropertyMutation();

  const handleSubmitEnumValues =
    (values: DefinitionFormValues) =>
    async (
      definition: TenantFieldDefinitionResponseBody
    ): Promise<TenantFieldDefinitionResponseBody> => {
      const isEnum = values.valueType === 'enum' || values.valueType === 'multi_enum';
      const enumValues = values.enumValues ?? [];

      if (!isEnum || isNil(enumValues) || isEmpty(enumValues)) {
        return definition;
      }

      const promises = enumValues.map(({value}, priority) =>
        addTenantEnumOption({
          definitionId: definition.id,
          resourceId: props.resourceId,
          addEnumOptionRequestBody: {name: value, priority},
        })
      );

      await Promise.all(promises).catch(handleApiError);

      return definition;
    };

  const handleSubmitPropertyName =
    (values: DefinitionFormValues) => async (definition: TenantFieldDefinitionResponseBody) => {
      if (isNilOrEmpty(values.propertyName)) {
        return definition;
      }

      await createCustomFieldProperty({
        customFieldDefinitionId: definition.id,
        createCustomFieldPropertyRequestBody: {propertyName: values.propertyName},
      })
        .unwrap()
        .catch(handleApiError);

      return definition;
    };

  const handleCreateDefinition: FormSubmitHandler<DefinitionFormValues> = async (submitData) => {
    const body: AddFieldDefinitionRequestBody = {
      hint: submitData.hint,
      name: submitData.name,
      valueType: submitData.valueType,
      allowedValues: submitData.allowedValues.map((value) => ({type: 'string', value})) ?? [],
      resourceId: props.resourceId,
      isRequired: submitData.isRequired,
      isNotified: submitData.isNotified,
      rules: [],
      defaultValue:
        submitData.valueType === 'boolean'
          ? transformToBoolean(submitData.defaultValue)
          : undefined,
    };

    await createDefinition({body})
      .unwrap()
      .then(handleSubmitEnumValues(submitData))
      .then(handleSubmitPropertyName(submitData))
      // NOTE: refetching manually so form leaves loading state only after new data is fetched
      .then(refetch)
      .then(closeCurrentDialog)
      .then(props.onAfterSubmit)
      .catch(handleApiError);
  };

  return (
    <Form<DefinitionFormValues>
      schema={schema}
      onSubmit={handleCreateDefinition}
      defaultValues={{enumValues: [EMPTY_ENUM_VALUE], defaultValue: 'null'}}
    >
      {(control, formApi) => {
        const selectedValueType = formApi.watch('valueType');

        return (
          <VStack spacing={4}>
            <Grid columns={3}>
              <FormField
                type="checkbox"
                control={control}
                name="isRequired"
                label={i18n.t('page.generalSettings.labels.isFieldRequired')}
                data-testid={suffixTestId('isFieldRequired', props)}
              />

              <Show when={['date', 'datetime'].includes(selectedValueType)}>
                <FormField
                  type="checkbox"
                  control={control}
                  name="isNotified"
                  label={i18n.t('page.generalSettings.labels.isNotified')}
                  data-testid={suffixTestId('isNotified', props)}
                />
              </Show>
            </Grid>
            <Separator spacing={0} />
            <Grid columns={2}>
              <FormField
                type="choice"
                control={control}
                options={VALUE_TYPE_OPTIONS}
                onChange={() => {
                  formApi.setValue('allowedValues', []);
                }}
                name="valueType"
                label={i18n.t('page.generalSettings.labels.valueType')}
                data-testid={suffixTestId('valueType', props)}
                isNotClearable
                menuInPortal
                isRequired
              />

              <FormField
                type="text"
                control={control}
                name="name"
                label={i18n.t('page.generalSettings.labels.name')}
                data-testid={suffixTestId('name', props)}
                isRequired
              />

              <FormField
                type="text"
                control={control}
                name="hint"
                label={i18n.t('page.generalSettings.labels.hint')}
                data-testid={suffixTestId('hint', props)}
                isRequired
              />

              <FormField
                type="text"
                control={control}
                name="propertyName"
                label={i18n.t('page.generalSettings.actions.updateTemplateValue')}
                data-testid={suffixTestId('propertyName', props)}
              />

              <Show when={selectedValueType === 'boolean'}>
                <FormField
                  type="choice"
                  control={control}
                  name="defaultValue"
                  label={i18n.t('page.generalSettings.labels.defaultValue')}
                  helperText={i18n.t('page.generalSettings.labels.defaultValueHelperText')}
                  options={DEFAULT_VALUE_OPTIONS}
                  data-testid={suffixTestId('defaultValue', props)}
                  isNotClearable
                  isRequired
                  menuInPortal
                />
              </Show>
            </Grid>

            <AdditionalFieldsByType
              control={control}
              formApi={formApi}
              data-testid={suffixTestId('additionalFields', props)}
            />

            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discardChanges')}
                variant="secondary"
                onClick={closeCurrentDialog}
                data-testid={suffixTestId('discardChanges', props)}
              />
              <FormButton
                leftIcon="content/save"
                title={i18n.t('general.actions.saveChanges')}
                control={control}
                data-testid={suffixTestId('save', props)}
                type="submit"
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}

const schema = object({
  name: yupString.required(),
  valueType: yupString.oneOf(VALUE_TYPE_OPTIONS.map((option) => option.value)).required(),
  hint: yupString.required(),
  isRequired: boolean().default(false),
  propertyName: yupString.test(
    'no-whitespace',
    i18n.t('general.errors.string.whiteSpace'),
    (value) => !value || !/\s/.test(value)
  ),
  isNotified: boolean().default(false),
  enumValues: array()
    .when('valueType', {
      is: 'enum',
      then: (schema) => schema.of(object({value: yupString.required()})),
    })
    .when('valueType', {
      is: 'multi_enum',
      then: (schema) =>
        schema.of(
          object({
            value: yupString
              .required()
              .test(
                'no-comma',
                i18n.t('page.generalSettings.labels.includesCommaMessage'),
                (value) => !value || !value.includes(',')
              ),
          })
        ),
    })
    .default([]),
  defaultValue: yupString.oneOf(DEFAULT_VALUE_OPTIONS.map((option) => option.value)).optional(),
});
